import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import API from '../api';
import { SellerSpinner } from '../components/skeleton';
import CampaignDetailChart from '../components/CampaignDetailChart';
import MerchantAccountModal from '../components/MerchantAccountModal';
import AdvertisingCostsTable from '../components/AdvertisingCostsTable';
import { AuthContext } from '../components/AuthContext';
import '../styles/SellerCommon.css';
import './SellerGoogleAdsCampaigns.css';

function SellerGoogleAdsCampaigns() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [prerequisites, setPrerequisites] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMerchantModal, setShowMerchantModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Costante per il form di default
  const today = new Date().toISOString().split('T')[0];
  const DEFAULT_CREATE_FORM = {
    campaignName: '',
    budget: '',
    startDate: today,
    endDate: '',
    biddingStrategy: 'maximize_clicks',
    biddingValue: '',
    merchantAccountId: ''
  };
  
  const [createForm, setCreateForm] = useState(DEFAULT_CREATE_FORM);
  const [editForm, setEditForm] = useState({
    campaignName: '',
    budget: '',
    endDate: '',
    status: 'active',
    biddingStrategy: 'maximize_clicks',
    biddingValue: ''
  });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success'); // 'success' | 'error'
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showConversionTrackingModal, setShowConversionTrackingModal] = useState(false);
  const [syncingProducts, setSyncingProducts] = useState(false);
  const [walletInfo, setWalletInfo] = useState(null);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [autoRechargeEnabled, setAutoRechargeEnabled] = useState(false);
  const [walletSubmitting, setWalletSubmitting] = useState(false);

  useEffect(() => {
    loadPrerequisites();
    loadCampaigns();
    loadWalletInfo();
  }, []);

  useEffect(() => {
    if (walletInfo) {
      setAutoRechargeEnabled(walletInfo.auto_recharge_enabled || false);
    }
  }, [walletInfo]);

  // Precompila merchantAccountId quando i prerequisiti sono caricati
  useEffect(() => {
    if (prerequisites?.prerequisites?.merchantAccount?.data?.account_id && !createForm.merchantAccountId) {
      setCreateForm(prev => ({
        ...prev,
        merchantAccountId: prerequisites.prerequisites.merchantAccount.data.account_id
      }));
    }
  }, [prerequisites, createForm.merchantAccountId]);

  const loadPrerequisites = async () => {
    try {
      const response = await API.get('/seller/google-ads/prerequisites');
      setPrerequisites(response.data);
    } catch (error) {
      console.error('Errore nel caricamento prerequisiti:', error);
      setMessage('Errore nel caricamento dei prerequisiti');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await API.get('/seller/google-ads/campaigns');
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Errore nel caricamento campagne:', error);
      setMessage('Errore nel caricamento delle campagne');
      setMessageType('error');
    }
  };

  const loadWalletInfo = async () => {
    try {
      const response = await API.get('/seller/google-ads/wallet');
      setWalletInfo(response.data.wallet);
    } catch (error) {
      console.error('Errore nel caricamento informazioni wallet:', error);
      // Non mostrare errore se il wallet non è ancora inizializzato
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    
    // Validazione frontend
    if (createForm.biddingStrategy === 'manual_cpc' && (!createForm.biddingValue || parseFloat(createForm.biddingValue) <= 0)) {
      setMessage('Per la strategia CPC Manuale è necessario specificare un valore di offerta valido');
      setMessageType('error');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await API.post('/seller/google-ads/campaigns', createForm);
      
      if (response.data.success) {
        setMessage('Campagna creata con successo!');
        setMessageType('success');
        setShowCreateModal(false);
        setCreateForm(DEFAULT_CREATE_FORM);
        loadCampaigns();
      }
    } catch (error) {
      console.error('Errore nella creazione campagna:', error);
      
      // Gestione specifica per l'errore NOT_ENOUGH_CONVERSIONS
      if (error.response?.data?.errorCode === 'NOT_ENOUGH_CONVERSIONS') {
        setMessage(error.response.data.message);
        setMessageType('error');
      } else {
        setMessage(error.response?.data?.message || 'Errore nella creazione della campagna');
        setMessageType('error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setCreateForm(prev => {
      if (name === 'biddingStrategy') {
        return { ...prev, biddingStrategy: value, biddingValue: '' };
      }
      return { ...prev, [name]: value };
    });
  }, []);

  const handleEditInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setEditForm(prev => {
      if (name === 'biddingStrategy') {
        return { ...prev, biddingStrategy: value, biddingValue: '' };
      }
      return { ...prev, [name]: value };
    });
  }, []);

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setEditForm({
      campaignName: campaign.name,
      budget: campaign.budget,
      endDate: campaign.endDate || '',
      status: campaign.status,
      biddingStrategy: campaign.biddingStrategy,
      biddingValue: campaign.biddingValue || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateCampaign = async (e) => {
    e.preventDefault();
    
    if (!editingCampaign) return;
    
    // Validazione frontend
    if (editForm.biddingStrategy === 'manual_cpc' && (!editForm.biddingValue || parseFloat(editForm.biddingValue) <= 0)) {
      setMessage('Per la strategia CPC Manuale è necessario specificare un valore di offerta valido');
      setMessageType('error');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await API.put(`/seller/google-ads/campaigns/${editingCampaign.campaign_id}`, editForm);
      
      if (response.data.success) {
        setMessage('Campagna aggiornata con successo!');
        setMessageType('success');
        setShowEditModal(false);
        setEditingCampaign(null);
        loadCampaigns(); // Ricarica la lista
        
        // Mostra messaggio specifico se c'è stato un addebito per aumento budget
        if (parseFloat(editForm.budget) > parseFloat(editingCampaign.budget || 0)) {
          setMessage('Campagna aggiornata! Il wallet è stato ricaricato per l\'aumento del budget.');
          setMessageType('success');
        }
      }
    } catch (error) {
      console.error('Errore nell\'aggiornamento campagna:', error);
      
      // Gestione specifica per l'errore NOT_ENOUGH_CONVERSIONS
      if (error.response?.data?.errorCode === 'NOT_ENOUGH_CONVERSIONS') {
        setMessage(error.response.data.message);
        setMessageType('error');
      } else {
        setMessage(error.response?.data?.message || 'Errore nell\'aggiornamento della campagna');
        setMessageType('error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleForceSync = async () => {
    try {
      setSyncingProducts(true);
      const response = await API.post('/seller/google-ads/force-sync');
      
      if (response.data.success) {
        setMessage('Sincronizzazione prodotti avviata');
        setMessageType('success');
        // Ricarica i prerequisiti dopo un breve delay
        setTimeout(() => {
          loadPrerequisites();
        }, 2000);
      }
    } catch (error) {
      console.error('Errore nella sincronizzazione:', error);
      setMessage('Errore nell\'avvio della sincronizzazione');
      setMessageType('error');
    } finally {
      setSyncingProducts(false);
    }
  };

  const handleToggleCampaignStatus = async (campaignId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      const response = await API.patch(`/seller/google-ads/campaigns/${campaignId}/status`, {
        status: newStatus
      });
      
      if (response.data.success) {
        setMessage(`Campagna ${newStatus === 'active' ? 'attivata' : 'messa in pausa'} con successo!`);
        setMessageType('success');
        loadCampaigns();
      }
    } catch (error) {
      console.error('Errore nel cambio stato campagna:', error);
      
      // Gestione specifica per fondi insufficienti
      if (error.response?.data?.insufficient_funds) {
        const { current_balance, required_balance } = error.response.data;
        setMessage(`Fondi insufficienti per attivare la campagna. Saldo: ${current_balance.toFixed(2)}€, Richiesto: ${required_balance.toFixed(2)}€. Ricarica il wallet per continuare.`);
        setMessageType('error');
        
        // Ricarica info wallet per aggiornare il saldo visualizzato
        loadWalletInfo();
      } else if (error.response?.data?.message) {
        setMessage(error.response.data.message);
        setMessageType('error');
      } else {
        setMessage('Errore nel cambio stato della campagna');
        setMessageType('error');
      }
    }
  };

  const handleDeleteCampaign = async (campaignId, campaignName) => {
    if (!window.confirm(`Sei sicuro di voler eliminare la campagna "${campaignName}"? Questa azione non può essere annullata.`)) {
      return;
    }
    
    try {
      const response = await API.delete(`/seller/google-ads/campaigns/${campaignId}`);
      
      if (response.data.success) {
        setMessage('Campagna eliminata con successo!');
        setMessageType('success');
        loadCampaigns();
      }
    } catch (error) {
      console.error('Errore nell\'eliminazione campagna:', error);
      setMessage('Errore nell\'eliminazione della campagna');
      setMessageType('error');
    }
  };

  const handleUpdateAutoRecharge = async (autoRechargeEnabled) => {
    try {
      const response = await API.put('/seller/google-ads/wallet/auto-recharge', {
        autoRechargeEnabled
      });
      
      if (response.data.success) {
        setMessage('Impostazioni autoricarica aggiornate con successo!');
        setMessageType('success');
        loadWalletInfo(); // Ricarica le informazioni del wallet
        setShowWalletModal(false);
      }
    } catch (error) {
      console.error('Errore nell\'aggiornamento autoricarica:', error);
      setMessage(error.response?.data?.message || 'Errore nell\'aggiornamento delle impostazioni');
      setMessageType('error');
    }
  };

  const PrerequisitesCard = useCallback(({ prerequisite, title, actionLink, actionText, actionClick, stepNumber }) => (
    <div className={`prerequisite-card ${prerequisite.satisfied ? 'satisfied' : 'not-satisfied'}`}>
      <div className="prerequisite-header">
        <div className="prerequisite-step-number">{stepNumber}</div>
        <h3>{title}</h3>
      </div>
      <p className="prerequisite-message">{prerequisite.message}</p>
            
      {!prerequisite.satisfied && actionLink && (
        <Link to={actionLink} className="prerequisite-action">
          {actionText}
        </Link>
      )}
      {!prerequisite.satisfied && actionClick && (
        <button onClick={actionClick} className="prerequisite-action">
          {actionText}
        </button>
      )}
    </div>
  ), []);

  const PrerequisitesSummary = useCallback(() => {
    const billingInfo = prerequisites.prerequisites.billing;
    const missingFields = billingInfo?.missingFields;
    
    return (
      <div className="prerequisites-summary">
        <div className="summary-grid">
          <div className="summary-item">
            <strong>Prodotti:</strong> {prerequisites.summary.productsCount} prodotti
          </div>
          <div className="summary-item">
            <strong>Merchant:</strong> {prerequisites.summary.merchantName || 'Non configurato'}
          </div>
          <div className="summary-item">
            <strong>Fatturazione:</strong> {prerequisites.prerequisites.billing.satisfied ? 
              prerequisites.summary.businessName : 'Incompleta'}
            {!prerequisites.prerequisites.billing.satisfied && missingFields && (missingFields.required?.length > 0 || missingFields.tax?.length > 0) && (
              <div className="missing-fields">
                <small>
                  <strong>Campi mancanti:</strong><br/>
                  {missingFields.required?.length > 0 && (
                    <>Campi obbligatori: {missingFields.required.join(', ')}<br/></>
                  )}
                  {missingFields.tax?.length > 0 && (
                    <>Codice Univoco o PEC: {missingFields.tax.join(' o ')}</>
                  )}
                </small>
              </div>
            )}
          </div>
          <div className="summary-item">
            <strong>Spedizione:</strong> {prerequisites.prerequisites.shippingRates.count} tariffe configurate
          </div>
          <div className="summary-item">
            <strong>Pagamento:</strong> {prerequisites.prerequisites.paymentMethod.satisfied ? 
              prerequisites.prerequisites.paymentMethod.message : 'Non collegato'}
          </div>
        </div>
      </div>
    );
  }, [prerequisites]);

  const WalletInfo = useCallback(() => {
    if (!walletInfo) return null;
    
    return (
      <div className="wallet-info-section">
        <div className="wallet-card">
          <div className="wallet-header">
            <h3>Credito Pubblicitario</h3>
            <button 
              className="seller-ads-btn seller-ads-btn-outline seller-ads-btn-small"
              onClick={() => setShowWalletModal(true)}
            >
              Gestisci Autoricarica
            </button>
          </div>
          <div className="wallet-details">
            <div className="wallet-balance">
              <span className="balance-label">Saldo Attuale:</span>
              <span className={`balance-amount ${walletInfo.current_balance < 10 ? 'low-balance' : ''}`}>
                €{walletInfo.current_balance.toFixed(2)}
              </span>
            </div>
            <div className="wallet-stats">
              <div className="stat-item">
                <span className="stat-label">Campagne Attive:</span>
                <span className="stat-value">{walletInfo.active_campaigns_count}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Budget Giornaliero Totale:</span>
                <span className="stat-value">€{walletInfo.total_daily_budget.toFixed(2)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Spesa degli ultimi 7 giorni:</span>
                <span className="stat-value">€{walletInfo.total_spent_7_days.toFixed(2)}</span>
              </div>
            </div>
            <div className="auto-recharge-status">
              <span className="status-label">Autoricarica:</span>
              <span className={`status-value ${walletInfo.auto_recharge_enabled ? 'enabled' : 'disabled'}`}>
                {walletInfo.auto_recharge_enabled ? 'Abilitata' : 'Disabilitata'}
              </span>
            </div>
            {walletInfo.current_balance < walletInfo.total_daily_budget && (
              <div className="low-balance-warning">
                <strong>⚠️ Attenzione:</strong> Il saldo è insufficiente per coprire il budget giornaliero delle campagne attive.
                {!walletInfo.auto_recharge_enabled && (
                  <span> Abilita l'autoricarica per evitare l'interruzione delle campagne.</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [walletInfo]);

  // Funzione pura per renderizzare il modal di creazione (senza useCallback per evitare defocus)
  const renderCreateCampaignModal = () => (
    <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Crea Nuova Campagna Google Shopping</h2>
          <button 
            className="modal-close" 
            onClick={() => setShowCreateModal(false)}
          >
            ×
          </button>
        </div>
        <form onSubmit={handleCreateCampaign} className="campaign-form">
          <div className="form-group">
            <label htmlFor="campaignName">Nome Campagna *</label>
            <input
              type="text"
              id="campaignName"
              name="campaignName"
              value={createForm.campaignName}
              onChange={handleInputChange}
              required
              placeholder="Es: Campagna Estate 2024"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="budget">Budget Giornaliero (€) *</label>
            <input
              type="number"
              id="budget"
              name="budget"
              value={createForm.budget}
              onChange={handleInputChange}
              required
              min="1"
              step="0.01"
              placeholder="50.00"
            />
            {createForm.budget && parseFloat(createForm.budget) > 0 && (
              <div className="budget-info">
                <small>
                  <strong>Addebito previsto:</strong> 
                  {(() => {
                    const dailyBudget = parseFloat(createForm.budget);
                    const startDate = new Date(createForm.startDate);
                    const endDate = createForm.endDate ? new Date(createForm.endDate) : null;
                    
                    // Calcola i giorni effettivi (max 7)
                    let daysToCharge = 7;
                    if (endDate) {
                      const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
                      daysToCharge = Math.min(daysDiff, 7);
                    }
                    
                    const subtotal = dailyBudget * daysToCharge;
                    const iva = subtotal * 0.22; // 22% IVA
                    const total = subtotal + iva;
                    
                    return (
                      <>
                        <br/>
                        • Budget giornaliero: €{dailyBudget.toFixed(2)}
                        <br/>
                        • Giorni addebitati: {daysToCharge} {daysToCharge < 7 && endDate ? '(fino alla fine campagna)' : '(primi 7 giorni)'}
                        <br/>
                        • Subtotale: €{subtotal.toFixed(2)}
                        <br/>
                        • IVA (22%): €{iva.toFixed(2)}
                        <br/>
                        • <strong>Totale addebito: €{total.toFixed(2)}</strong>
                      </>
                    );
                  })()}
                </small>
              </div>
            )}
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startDate">Data Inizio *</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={createForm.startDate}
                onChange={handleInputChange}
                required
                readOnly
                title="La data di inizio è impostata automaticamente a oggi"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="endDate">Data Fine (Opzionale)</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={createForm.endDate}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="merchantAccountId">Merchant Account *</label>
            <select
              id="merchantAccountId"
              name="merchantAccountId"
              value={createForm.merchantAccountId}
              onChange={handleInputChange}
              required
            >
              <option value="">Seleziona un merchant account</option>
              {prerequisites?.prerequisites?.merchantAccount?.data && (
                <option value={prerequisites.prerequisites.merchantAccount.data.account_id}>
                  {prerequisites.prerequisites.merchantAccount.data.account_name}
                </option>
              )}
            </select>
            <small className="form-help">
              <strong>📍 Targeting Geografico:</strong> La campagna sarà automaticamente configurata per mostrare annunci in Italia.
            </small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="biddingStrategy">Strategia di Offerta *</label>
              <select
                id="biddingStrategy"
                name="biddingStrategy"
                value={createForm.biddingStrategy}
                onChange={handleInputChange}
                required
              >
                <option value="maximize_clicks">Massimizza Click</option>
                <option value="manual_cpc">CPC Manuale</option>
                <option value="target_roas">Target ROAS</option>
              </select>
            </div>
            
            {createForm.biddingStrategy !== 'target_roas' && createForm.biddingStrategy !== 'maximize_conversions' && (
              <div className="form-group">
                <label htmlFor="biddingValue">
                  {createForm.biddingStrategy === 'maximize_clicks' ? 'CPC Max (Opzionale)' : 'Valore *'}
                </label>
                <input
                  type="number"
                  id="biddingValue"
                  name="biddingValue"
                  value={createForm.biddingValue}
                  onChange={handleInputChange}
                  min={createForm.biddingStrategy === 'maximize_clicks' ? 0 : 0.01}
                  step="0.01"
                  required={createForm.biddingStrategy === 'manual_cpc'}
                  placeholder={
                    createForm.biddingStrategy === 'maximize_clicks' ? '0.15€' :
                    createForm.biddingStrategy === 'manual_cpc' ? '0.50€' : '0.01€'
                  }
                />
              </div>
            )}

            {createForm.biddingStrategy === 'target_roas' && (
              <div className="form-group">
                <label htmlFor="biddingValue">Target ROAS (es. 4.0 = 400%)</label>
                <input
                  type="number"
                  id="biddingValue"
                  name="biddingValue"
                  value={createForm.biddingValue}
                  onChange={handleInputChange}
                  min="0.1"
                  step="0.1"
                  placeholder="4.0"
                />
              </div>
            )}
          </div>

          {/* Spiegazione Strategia di Offerta */}
          <div className="bidding-strategy-explanation">
            {createForm.biddingStrategy === 'manual_cpc' && (
              <div className="strategy-info">
                <h4>CPC Manuale</h4>
                <p>Controlli manualmente il costo massimo per ogni clic. Il <strong>Valore</strong> rappresenta il CPC massimo in euro che sei disposto a pagare per ogni clic sui tuoi annunci.</p>
                <p><strong>Ideale per:</strong> Controllo totale sui costi e quando hai esperienza nella gestione delle offerte.</p>
              </div>
            )}
            
            {createForm.biddingStrategy === 'target_roas' && (
              <div className="strategy-info">
                <h4>Target ROAS</h4>
                <p>Google ottimizza automaticamente le offerte per raggiungere un ritorno sulla spesa pubblicitaria target. Il <strong>Valore</strong> rappresenta il ROAS target (es: 400 = 4€ di ricavo per ogni 1€ speso).</p>
                <p><strong>Ideale per:</strong> Quando vuoi un ritorno specifico sugli investimenti pubblicitari e hai dati storici di conversione.</p>
                <div className="target-roas-warning">
                  <p><strong>⚠️ Attenzione:</strong> Target ROAS richiede almeno 15 conversioni negli ultimi 30 giorni. Se non hai ancora abbastanza conversioni, inizia con "Massimizza Click" e passa a Target ROAS dopo aver accumulato dati di conversione.</p>
                </div>
              </div>
            )}
            
            {createForm.biddingStrategy === 'maximize_clicks' && (
              <div className="strategy-info">
                <h4>Massimizza Click</h4>
                <p>Google utilizza il machine learning per ottenere il maggior numero di click possibili con il tuo budget. Puoi opzionalmente impostare un <strong>CPC Max</strong> per limitare il costo massimo per click.</p>
                <p><strong>Ideale per:</strong> Quando vuoi il massimo volume di traffico possibile verso il tuo sito web.</p>
              </div>
            )}
          </div>
          
          <div className="form-actions">
            <button type="button" className="seller-ads-btn seller-ads-btn-outline" onClick={() => setShowCreateModal(false)}>
              Annulla
            </button>
            <button type="submit" className="seller-ads-btn seller-ads-btn-primary" disabled={submitting}>
              {submitting ? 'Creazione in corso...' : 'Crea Campagna'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Funzione pura per renderizzare il modal di modifica (senza useCallback per evitare defocus)
  const renderEditCampaignModal = () => (
    <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Modifica Campagna: {editingCampaign?.name}</h2>
          <button 
            className="modal-close" 
            onClick={() => setShowEditModal(false)}
          >
            ×
          </button>
        </div>
        <form onSubmit={handleUpdateCampaign} className="campaign-form">
          <div className="form-group">
            <label htmlFor="editCampaignName">Nome Campagna *</label>
            <input
              type="text"
              id="editCampaignName"
              name="campaignName"
              value={editForm.campaignName}
              onChange={handleEditInputChange}
              required
              placeholder="Es: Campagna Estate 2024"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="editBudget">Budget Giornaliero (€) *</label>
              <input
                type="number"
                id="editBudget"
                name="budget"
                value={editForm.budget}
                onChange={handleEditInputChange}
                required
                min="1"
                step="0.01"
                placeholder="50.00"
              />
              {parseFloat(editForm.budget) > parseFloat(editingCampaign?.budget || 0) && (
                <small className="budget-warning">
                  <strong>💰 Aumento budget - Addebito previsto:</strong>
                  {(() => {
                    const currentBudget = parseFloat(editingCampaign?.budget || 0);
                    const newBudget = parseFloat(editForm.budget);
                    const budgetIncrease = newBudget - currentBudget;
                    
                    // Calcola i giorni rimanenti (max 7)
                    const today = new Date();
                    const endDate = editForm.endDate ? new Date(editForm.endDate) : null;
                    
                    let daysToCharge = 7;
                    if (endDate) {
                      const daysDiff = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
                      daysToCharge = Math.max(0, Math.min(daysDiff, 7));
                    }
                    
                    const subtotal = budgetIncrease * daysToCharge;
                    const iva = subtotal * 0.22;
                    const total = subtotal + iva;
                    
                    return (
                      <>
                        <br/>
                        • Aumento giornaliero: €{budgetIncrease.toFixed(2)}
                        <br/>
                        • Giorni addebitati: {daysToCharge} {daysToCharge < 7 && endDate ? '(fino alla fine campagna)' : '(prossimi 7 giorni)'}
                        <br/>
                        • Subtotale: €{subtotal.toFixed(2)}
                        <br/>
                        • IVA (22%): €{iva.toFixed(2)}
                        <br/>
                        • <strong>Totale addebito: €{total.toFixed(2)}</strong>
                      </>
                    );
                  })()}
                </small>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="editEndDate">Data Fine (Opzionale)</label>
              <input
                type="date"
                id="editEndDate"
                name="endDate"
                value={editForm.endDate}
                onChange={handleEditInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="editStatus">Stato Campagna *</label>
              <select
                id="editStatus"
                name="status"
                value={editForm.status}
                onChange={handleEditInputChange}
                required
              >
                <option value="active">Attiva</option>
                <option value="paused">In Pausa</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="editBiddingStrategy">Strategia di Offerta *</label>
              <select
                id="editBiddingStrategy"
                name="biddingStrategy"
                value={editForm.biddingStrategy}
                onChange={handleEditInputChange}
                required
              >
                <option value="maximize_clicks">Massimizza Click</option>
                <option value="manual_cpc">CPC Manuale</option>
                <option value="target_roas">Target ROAS</option>
              </select>
            </div>
          </div>

          {editForm.biddingStrategy !== 'target_roas' && editForm.biddingStrategy !== 'maximize_conversions' && (
            <div className="form-group">
              <label htmlFor="editBiddingValue">
                {editForm.biddingStrategy === 'maximize_clicks' ? 'CPC Max (Opzionale)' : 'Valore *'}
              </label>
              <input
                type="number"
                id="editBiddingValue"
                name="biddingValue"
                value={editForm.biddingValue}
                onChange={handleEditInputChange}
                min={editForm.biddingStrategy === 'maximize_clicks' ? 0 : 0.01}
                step="0.01"
                required={editForm.biddingStrategy === 'manual_cpc'}
                placeholder={
                  editForm.biddingStrategy === 'maximize_clicks' ? '0.15€' :
                  editForm.biddingStrategy === 'manual_cpc' ? '0.50€' : '0.01€'
                }
              />
            </div>
          )}

          {editForm.biddingStrategy === 'target_roas' && (
            <div className="form-group">
              <label htmlFor="editBiddingValue">Target ROAS (es. 4.0 = 400%)</label>
              <input
                type="number"
                id="editBiddingValue"
                name="biddingValue"
                value={editForm.biddingValue}
                onChange={handleEditInputChange}
                min="0.1"
                step="0.1"
                placeholder="4.0"
              />
              <div className="target-roas-warning">
                <small><strong>⚠️ Attenzione:</strong> Target ROAS richiede almeno 15 conversioni negli ultimi 30 giorni. Se non hai ancora abbastanza conversioni, usa "Massimizza Click".</small>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="seller-ads-btn seller-ads-btn-outline" onClick={() => setShowEditModal(false)}>
              Annulla
            </button>
            <button type="submit" className="seller-ads-btn seller-ads-btn-primary" disabled={submitting}>
              {submitting ? 'Aggiornamento in corso...' : 'Aggiorna Campagna'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Funzione pura per renderizzare il modal di tracciamento conversioni (senza useCallback per evitare defocus)
  const renderConversionTrackingModal = () => (
    <div className="modal-overlay" onClick={() => setShowConversionTrackingModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Tracciamento Conversioni</h2>
          <button 
            className="modal-close" 
            onClick={() => setShowConversionTrackingModal(false)}
          >
            ×
          </button>
        </div>
        <div className="conversion-tracking-content">
          <h3>Implementazione Tracciamento Conversioni</h3>
          
          <div className="step">
            <h4>1. Aggiungi il codice Google Tag al tuo sito</h4>
            <p>Inserisci questo codice nell'<code>&lt;head&gt;</code> del tuo sito web:</p>
            <div className="code-block">
              <pre><code>{`<!-- Google tag (gtag.js) -->
<script async src="https://www.googletagmanager.com/gtag/js?id=AW-17382365583"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());

  gtag('config', 'AW-17382365583');
</script>`}</code></pre>
            </div>
          </div>

          <div className="step">
            <h4>2. Aggiungi il codice di conversione alla pagina di ringraziamento</h4>
            <p>Inserisci questo codice nella pagina di conferma ordine:</p>
            <div className="code-block">
              <pre><code>{`<!-- Event snippet for Acquisto conversion page -->
<script>
  gtag('event', 'conversion', {
      'send_to': 'AW-17382365583/CmOwCKnOnIQbEI-zx-BA',
      'value': 1.0,
      'currency': 'EUR',
      'transaction_id': ''
  });
</script>`}</code></pre>
            </div>
            <p><strong>Nota:</strong> Sostituisci il valore di <code>transaction_id</code> con l'ID del tuo ordine.</p>
          </div>

          <div className="note">
            <p><strong>Importante:</strong> Una volta implementato il tracciamento, potrai utilizzare strategie di offerta avanzate come Target CPA e Target ROAS per ottimizzare automaticamente le tue campagne.</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Funzione pura per renderizzare il modal di gestione autoricarica
  const renderWalletModal = () => {
    const handleSubmit = async (e) => {
      e.preventDefault();
      setWalletSubmitting(true);
      
      await handleUpdateAutoRecharge(autoRechargeEnabled);
      setWalletSubmitting(false);
    };

    return (
      <div className="modal-overlay" onClick={() => setShowWalletModal(false)}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Gestione Autoricarica Wallet</h2>
            <button 
              className="modal-close" 
              onClick={() => setShowWalletModal(false)}
            >
              ×
            </button>
          </div>
          <form onSubmit={handleSubmit} className="wallet-form">
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={autoRechargeEnabled}
                  onChange={(e) => setAutoRechargeEnabled(e.target.checked)}
                />
                <span>Abilita autoricarica automatica</span>
              </label>
              <small className="form-help">
                Quando abilitata, il wallet verrà ricaricato automaticamente quando il saldo scende sotto il budget giornaliero delle campagne attive.
              </small>
            </div>

            <div className="auto-recharge-info">
              <h4>Come funziona l'autoricarica:</h4>
              <ul>
                <li><strong>Se abilitata:</strong> Il wallet viene ricaricato automaticamente quando il saldo scende sotto il budget giornaliero</li>
                <li><strong>Se disabilitata:</strong> Le campagne si fermeranno quando il wallet è vuoto</li>
                <li><strong>Importo:</strong> Viene calcolato automaticamente per 7 giorni di budget giornaliero + IVA (22%)</li>
                <li><strong>Controllo:</strong> Ogni giorno alle 2:00 viene verificato il saldo</li>
              </ul>
            </div>

            <div className="form-actions">
              <button type="button" className="seller-ads-btn seller-ads-btn-outline" onClick={() => setShowWalletModal(false)}>
                Annulla
              </button>
              <button type="submit" className="seller-ads-btn seller-ads-btn-primary" disabled={walletSubmitting}>
                {walletSubmitting ? 'Salvataggio...' : 'Salva Impostazioni'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return <SellerSpinner />;
  }

  return (
    <div className="seller-google-ads-page">
      <div className="container">
        <div className="page-header">
          <h1>Campagne Google Shopping</h1>
          <p>Gestisci le tue campagne pubblicitarie su Google Shopping</p>
        </div>

        {message && (
          <div className={`alert alert-${messageType}`}>
            {message}
            <button onClick={() => setMessage('')} className="close-btn">&times;</button>
          </div>
        )}

      {/* Sezione Prerequisiti - nascondi se tutti soddisfatti */}
      {!prerequisites.allSatisfied && (
        <div className="prerequisites-section">
          <h2>Prerequisiti per Creare Campagne</h2>
          <div className="prerequisites-grid">
          <PrerequisitesCard
            prerequisite={prerequisites.prerequisites.products}
            title="Prodotti Caricati"
            actionLink="/seller-integration"
            actionText="Carica Prodotti"
            stepNumber="1"
          />
          
          <PrerequisitesCard
            prerequisite={prerequisites.prerequisites.merchantAccount}
            title="Merchant Account CSS"
            actionText="Configura Merchant"
            actionClick={() => setShowMerchantModal(true)}
            stepNumber="2"
          />
          
          <PrerequisitesCard
            prerequisite={prerequisites.prerequisites.billing}
            title="Informazioni di Fatturazione"
            actionLink="/billing"
            actionText="Completa Fatturazione"
            stepNumber="3"
          />
          
          <PrerequisitesCard
            prerequisite={prerequisites.prerequisites.shippingRates}
            title="Tariffe di Spedizione"
            actionLink="/shipping-rates"
            actionText="Configura Tariffe"
            stepNumber="4"
          />
          
          <PrerequisitesCard
            prerequisite={prerequisites.prerequisites.paymentMethod}
            title="Metodo di Pagamento"
            actionLink="/billing"
            actionText="Configura Pagamento"
            stepNumber="5"
          />
          </div>
        </div>
      )}

      {/* Sezione Sincronizzazione Prodotti - Solo per merchant appena creati */}
      {prerequisites.prerequisites.productSync && prerequisites.prerequisites.productSync.hasNeverSynced && (
        <div className="product-sync-section">
          <h3>Sincronizzazione Prodotti</h3>
          <div className="sync-status">
            <p>{prerequisites.prerequisites.productSync.message}</p>
            <button 
              className="seller-ads-btn seller-ads-btn-primary"
              onClick={handleForceSync}
              disabled={syncingProducts}
            >
              {syncingProducts ? 'Sincronizzazione in corso...' : 'Forza Sincronizzazione'}
            </button>
          </div>
        </div>
      )}

      {/* Sezione Riepilogo */}
      {prerequisites.allSatisfied && <PrerequisitesSummary />}
      {prerequisites.allSatisfied && <WalletInfo />}

      {/* Sezione Azioni */}
      <div className="actions-section">
        <div className="action-buttons">
          {prerequisites.allSatisfied ? (
            <button 
              className="seller-ads-btn seller-ads-btn-primary seller-ads-btn-large"
              onClick={() => setShowCreateModal(true)}
            >
              Crea nuova Campagna Google Shopping
            </button>
          ) : (
            <div className="prerequisites-warning">
              <p>Completa tutti i prerequisiti per creare una campagna</p>
            </div>
          )}
        </div>
        
        {/* Pulsanti aggiuntivi a destra */}
        <div className="additional-actions">
          
          <button 
            className="seller-ads-btn seller-ads-btn-outline"
            onClick={() => setShowConversionTrackingModal(true)}
          >
            Tracciamento Conversioni
          </button>
        </div>
      </div>

      {/* Sezione Campagne Esistenti */}
      {campaigns.length > 0 && (
        <div className="campaigns-section">
          <h2>Le Tue Campagne</h2>
          <div className="campaigns-grid">
            {campaigns.map(campaign => (
              <div key={campaign.campaign_id} className="campaign-card">
                <div className="campaign-header">
                  <h3>{campaign.name}</h3>
                  <span className={`campaign-status ${campaign.status}`}>
                    {campaign.status}
                  </span>
                </div>
                <div className="campaign-details">
                  <p><strong>Budget:</strong> €{campaign.budget}/giorno</p>
                  <p><strong>Periodo:</strong> {campaign.startDate} - {campaign.endDate || 'Nessuna data fine'}</p>
                  <p><strong>Strategia:</strong> {campaign.biddingStrategy}</p>
                  {campaign.biddingValue && (
                    <p><strong>Valore Offerta:</strong> €{campaign.biddingValue}</p>
                  )}
                </div>
                <div className="campaign-actions">
                  <button 
                    className="seller-ads-btn seller-ads-btn-outline"
                    onClick={() => setSelectedCampaign(campaign)}
                  >
                    Analytics
                  </button>
                  <button 
                    className="seller-ads-btn seller-ads-btn-primary"
                    onClick={() => handleEditCampaign(campaign)}
                  >
                    Modifica
                  </button>
                  <button 
                    className={`seller-ads-btn seller-ads-btn-outline ${campaign.status === 'active' ? 'seller-ads-btn-warning' : 'seller-ads-btn-success'}`}
                    onClick={() => handleToggleCampaignStatus(campaign.campaign_id, campaign.status)}
                  >
                    {campaign.status === 'active' ? 'Pausa' : 'Attiva'}
                  </button>
                  <button 
                    className="seller-ads-btn seller-ads-btn-outline seller-ads-btn-danger"
                    onClick={() => handleDeleteCampaign(campaign.campaign_id, campaign.name)}
                  >
                    Elimina
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modali */}
      {showCreateModal && renderCreateCampaignModal()}
      {showEditModal && renderEditCampaignModal()}
      {showConversionTrackingModal && renderConversionTrackingModal()}
      {showMerchantModal && (
        <MerchantAccountModal
          isOpen={showMerchantModal}
          onClose={() => setShowMerchantModal(false)}
          onSuccess={(merchant) => {
            setMessage('Merchant account creato con successo!');
            setMessageType('success');
            loadPrerequisites();
          }}
          sellerId={user?.userId}
          isSeller={true}
          sellerInfo={prerequisites?.prerequisites?.billing?.data}
        />
      )}
      
      {/* Analitiche Campagna */}
      {selectedCampaign && (
        <CampaignDetailChart
          campaign={selectedCampaign}
          startDate={selectedCampaign.startDate}
          endDate={selectedCampaign.endDate}
          onClose={() => setSelectedCampaign(null)}
        />
      )}

      {showWalletModal && renderWalletModal()}

      {/* Sezione Credito Pubblicitario */}
      <div className="advertising-credit-section">
        <AdvertisingCostsTable
          type="internal"
          title="Credito Pubblicitario - Campagne Loverlist"
          description="Costi delle campagne pubblicitarie gestite da Loverlist (markup 10%)"
        />
      </div>
        </div>
      </div>
    );
  }

  export default SellerGoogleAdsCampaigns; 